"use client";

import { useId, type ReactNode } from "react";
import { ChevronDown } from "lucide-react";
import { cn } from "@/lib/utils";
import { HOME_COPY } from "../home.copy";

/** Onde a lista corta. Seis chips e o card cabe na tela, no celular e no desktop. */
const VISIVEIS_ANTES_DO_CORTE = 6;

interface PlanFeatureListProps {
  /** Os chips já montados pelo card — esta lista só decide quem aparece. */
  items: ReactNode[];
  /**
   * O estado vem de fora, da vitrine, e é o mesmo para as três colunas — ver
   * o comentário do componente.
   */
  expanded: boolean;
  onToggle: () => void;
}

/**
 * A lista de opcionais do card de plano, com corte e "Ver mais".
 *
 * São dezoito chips. Empilhados de uma vez, a lista sozinha passava de uma tela
 * e meia e o card virava rolagem sem fim. Aqui os seis primeiros ficam à mostra
 * e o resto entra pelo botão.
 *
 * A altura anima por `grid-template-rows: 0fr → 1fr`, e não por `max-height`
 * chutado: é o que dispensa medir o conteúdo em JS, então a lista pode crescer
 * sem ninguém lembrar de atualizar um número. O `overflow-hidden` mora no
 * filho porque é a linha do grid que o corta.
 *
 * O componente não guarda o estado: quem guarda é a vitrine, e o mesmo estado
 * serve as três colunas. Duas razões, uma por breakpoint. No desktop as colunas
 * dividem a mesma linha de base — uma aberta ao lado de duas fechadas quebra o
 * alinhamento que topo, rodapé e CTAs dependem. E em qualquer largura, a lista
 * existe para comparar planos: comparar item a item só funciona se as três
 * colunas mostrarem os mesmos itens.
 */
export function PlanFeatureList({
  items,
  expanded,
  onToggle,
}: PlanFeatureListProps) {
  const idDaLista = useId();

  const aMostra = items.slice(0, VISIVEIS_ANTES_DO_CORTE);
  const extras = items.slice(VISIVEIS_ANTES_DO_CORTE);

  // Plano curto: sem corte, sem botão, sem véu.
  if (extras.length === 0) {
    return <div className="flex flex-col gap-1.5 md:gap-[7px]">{items}</div>;
  }

  return (
    <>
      <div className="relative">
        <div className="flex flex-col gap-1.5 md:gap-[7px]">{aMostra}</div>

        <div
          id={idDaLista}
          className={cn(
            "grid transition-[grid-template-rows] duration-[380ms] ease-[cubic-bezier(0.22,1,0.36,1)]",
            "motion-reduce:transition-none",
            expanded ? "grid-rows-[1fr]" : "grid-rows-[0fr]",
          )}
        >
          <div className="min-h-0 overflow-hidden">
            <div className="flex flex-col gap-1.5 pt-1.5 md:gap-[7px] md:pt-[7px]">
              {extras.map((item, index) => (
                <div
                  key={index}
                  className={cn(
                    "transition-[opacity,transform] duration-[320ms] ease-[cubic-bezier(0.22,1,0.36,1)]",
                    "motion-reduce:transition-none",
                    expanded ? "opacity-100" : "translate-y-1.5 opacity-0",
                  )}
                  style={{
                    // A cascata está aqui: cada chip entra 28ms depois do
                    // anterior. Do sétimo em diante o atraso congela — senão a
                    // cauda da lista chegaria meio segundo atrasada, e o que
                    // era ritmo viraria espera. Ao fechar, ninguém espera.
                    transitionDelay: expanded
                      ? `${60 + Math.min(index, 6) * 28}ms`
                      : "0ms",
                  }}
                >
                  {item}
                </div>
              ))}
            </div>
          </div>
        </div>

        {/* O corte não pode parecer o fim da lista: o véu apaga o último chip
            à mostra em vez de deixá-lo inteiro encostado no botão. */}
        <div
          aria-hidden
          className={cn(
            "pointer-events-none absolute inset-x-0 bottom-0 h-11 md:h-12",
            "bg-gradient-to-b from-transparent to-tami-dark",
            "transition-opacity duration-[260ms] motion-reduce:transition-none",
            expanded ? "opacity-0" : "opacity-100",
          )}
        />
      </div>

      <button
        type="button"
        onClick={onToggle}
        aria-expanded={expanded}
        aria-controls={idDaLista}
        className={cn(
          "mt-3 flex w-full items-center justify-center gap-1.5 md:mt-4",
          "border-t border-tami-light/15 pt-2.5 md:pt-3",
          "text-xs font-bold text-tami-green md:text-[13px]",
          "transition-colors hover:text-tami-light",
          "focus:outline-none focus-visible:ring-2 focus-visible:ring-tami-green focus-visible:ring-offset-2 focus-visible:ring-offset-tami-dark",
        )}
      >
        {expanded
          ? HOME_COPY.featureList.seeLess
          : HOME_COPY.featureList.seeMore(extras.length)}
        <ChevronDown
          className={cn(
            "size-3.5 transition-transform duration-[380ms] ease-[cubic-bezier(0.22,1,0.36,1)]",
            "motion-reduce:transition-none",
            expanded && "rotate-180",
          )}
          aria-hidden
        />
      </button>
    </>
  );
}
